import { GameState }    from '../../core/GameState.js';
import { EventBus }     from '../../core/EventBus.js';
import { SHIP_TYPES, SHIP_CATEGORIES } from '../../config/shipTypes.js';
import { CARGO_TYPES }  from '../../config/cargoTypes.js';
import { isUnlockCompleted, ALL_UNLOCKS } from '../../config/research.js';
import { pickShipName } from '../../config/shipNames.js';
import { Ship }         from '../../entities/Ship.js';
import { formatMoney }  from '../../utils/formatting.js';
import { showAlert, showConfirm } from '../components/dialogs.js';
import { serverIntent } from '../../core/serverIntent.js';
import { Modal }        from '../components/Modal.js';

/**
 * Ship Broker modal: browse, purchase, and sell ships.
 * Opened via EventBus 'ui:shipyardOpen'.
 */
export class ShipyardPanel {
  constructor() {
    this.modal = new Modal('shipyard-modal', { title: 'Ship Broker' });
    this._activeTab = 'buy';
    this._activeCategory = 'all';
    EventBus.on('ui:shipyardOpen', ({ port }) => this.open(port));
  }

  open(port) {
    this.port = port;
    this._activeTab = 'buy';
    this._activeCategory = 'all';
    this._render();
    this.modal.open();
  }

  // ── Main render ──────────────────────────────────────────
  _render() {
    const buyActive  = this._activeTab === 'buy'  ? 'active' : '';
    const sellActive = this._activeTab === 'sell' ? 'active' : '';

    let html = `
      <div class="broker-tabs">
        <button class="btab ${buyActive}"  data-broker-tab="buy">Buy Ships</button>
        <button class="btab ${sellActive}" data-broker-tab="sell">Sell Ships</button>
      </div>
      <div class="broker-funds">Funds: <strong>${formatMoney(GameState.player.money)}</strong></div>
      <div class="broker-content"></div>`;

    this.modal.setBody(html);

    // Tab switching
    this.modal.el.querySelectorAll('.btab').forEach(btn => {
      btn.addEventListener('click', () => {
        this._activeTab = btn.dataset.brokerTab;
        this._render();
      });
    });

    const content = this.modal.el.querySelector('.broker-content');
    if (this._activeTab === 'buy') {
      this._renderBuy(content);
    } else {
      this._renderSell(content);
    }
  }

  // ── Buy tab ──────────────────────────────────────────────
  _renderBuy(el) {
    // Category filter buttons
    let filterHtml = '<div class="broker-filters"><button class="bfilter ' +
      (this._activeCategory === 'all' ? 'active' : '') +
      '" data-cat="all">All</button>';
    for (const [catId, cat] of Object.entries(SHIP_CATEGORIES)) {
      const active = this._activeCategory === catId ? 'active' : '';
      filterHtml += `<button class="bfilter ${active}" data-cat="${catId}">${cat.label}</button>`;
    }
    filterHtml += '</div>';

    // Ship cards
    const types = Object.values(SHIP_TYPES)
      .filter(t => this._activeCategory === 'all' || t.category === this._activeCategory)
      .sort((a, b) => a.basePurchasePrice - b.basePurchasePrice);

    let cardsHtml = '<div class="broker-grid">';
    for (const type of types) {
      const unlockOk   = isUnlockCompleted(type.requiredUnlock);
      const canAfford  = GameState.player?.canAfford(type.basePurchasePrice);
      const locked     = !unlockOk;
      const catLabel   = SHIP_CATEGORIES[type.category]?.label || type.category;

      const restrictions = type.cargoRestrictions
        ? `<tr><td>Cargo</td><td>${type.cargoRestrictions.map(id => CARGO_TYPES[id]?.name || id).join(', ')}</td></tr>`
        : '<tr><td>Cargo</td><td>General cargo</td></tr>';

      cardsHtml += `<div class="broker-card ${locked ? 'locked' : ''}">
        <div class="broker-card-header">
          <h5>${type.name}</h5>
          <span class="broker-cat-badge">${catLabel}</span>
        </div>
        <p class="broker-desc">${type.description}</p>
        <table class="stats-table broker-stats">
          <tr><td>Capacity</td><td>${type.cargoCapacity.toLocaleString()} TEU</td></tr>
          <tr><td>Speed</td><td>${type.cruiseSpeed} kn (max ${type.maxSpeed})</td></tr>
          <tr><td>Fuel Tank</td><td>${type.maxFuel.toLocaleString()} t</td></tr>
          <tr><td>Fuel/day</td><td>${type.fuelConsumption} t</td></tr>
          <tr><td>Range</td><td>${type.range ? type.range.toLocaleString() + ' nm' : '—'}</td></tr>
          <tr><td>Daily Cost</td><td>${formatMoney(type.dailyOperatingCost)}</td></tr>
          <tr><td>Crew</td><td>${type.minCrewRequired}</td></tr>
          ${restrictions}
        </table>
        <div class="broker-price">${formatMoney(type.basePurchasePrice)}</div>
        ${locked
          ? `<span class="locked-label">Requires: ${ALL_UNLOCKS[type.requiredUnlock]?.name || type.requiredUnlock.replace(/_/g, ' ')}</span>`
          : `<button class="btn btn-primary btn-sm" data-buy-ship="${type.id}" ${!canAfford ? 'disabled' : ''}>Purchase</button>`
        }
      </div>`;
    }
    cardsHtml += '</div>';

    el.innerHTML = filterHtml + cardsHtml;

    // Category filter handlers
    el.querySelectorAll('.bfilter').forEach(btn => {
      btn.addEventListener('click', () => {
        this._activeCategory = btn.dataset.cat;
        this._render();
      });
    });

    // Buy handlers
    el.querySelectorAll('[data-buy-ship]').forEach(btn => {
      btn.addEventListener('click', () => {
        const typeId = btn.dataset.buyShip;
        this._buyShip(typeId);
      });
    });
  }

  // ── Sell tab ─────────────────────────────────────────────
  _renderSell(el) {
    // Ships docked at this port
    const dockedShips = GameState.getAllShips().filter(
      s => s.status === 'docked' && s.currentPortId === this.port.id
    );

    if (dockedShips.length === 0) {
      el.innerHTML = '<p class="panel-empty">No ships docked at this port to sell.</p>';
      return;
    }

    let html = '<div class="broker-sell-list">';
    for (const ship of dockedShips) {
      const value = this._getResaleValue(ship);
      const hasCargo = ship.cargo.length > 0;

      html += `<div class="broker-sell-card">
        <div class="broker-sell-header">
          <strong>${ship.name}</strong>
          <span class="ship-type">${ship.type?.name || 'Unknown'}</span>
        </div>
        <div class="broker-sell-details">
          <span>Condition: ${Math.round(ship.condition)}%</span>
          <span>Cargo: ${ship.cargoCapacityUsed}/${ship.getCargoCapacity()} TEU</span>
          <span>Fuel: ${Math.round(ship.fuel)}/${ship.maxFuel} t</span>
        </div>
        <div class="broker-sell-footer">
          <div class="broker-sell-value">
            <span class="broker-sell-label">Broker offer</span>
            <span class="broker-sell-price">${formatMoney(value)}</span>
          </div>
          ${hasCargo
            ? '<span class="broker-sell-warning">Unload cargo first</span>'
            : `<button class="btn btn-danger btn-sm" data-sell-ship="${ship.id}" data-sell-value="${value}">Sell Ship</button>`
          }
        </div>
      </div>`;
    }
    html += '</div>';

    el.innerHTML = html;

    // Sell handlers
    el.querySelectorAll('[data-sell-ship]').forEach(btn => {
      btn.addEventListener('click', () => {
        const shipId = btn.dataset.sellShip;
        const value  = Number(btn.dataset.sellValue);
        this._sellShip(shipId, value);
      });
    });
  }

  // ── Buy logic ────────────────────────────────────────────
  async _buyShip(typeId) {
    const typeDef = Object.values(SHIP_TYPES).find(t => t.id === typeId);
    if (!typeDef) return;
    if (!GameState.player?.canAfford(typeDef.basePurchasePrice)) {
      showAlert('Insufficient funds.');
      return;
    }
    // Send buy intent — server creates ship, pushes ship:purchased delta
    const result = await serverIntent('ship/buy', { shipType: typeId, portId: this.port.id });
    if (result.success) {
      this._render();
    }
  }

  // ── Sell logic ───────────────────────────────────────────
  _sellShip(shipId, value) {
    const ship = GameState.getShip(shipId);
    if (!ship) return;
    if (ship.cargo.length > 0) {
      showAlert('Unload all cargo before selling.');
      return;
    }

    showConfirm(`Sell ${ship.name} for ${formatMoney(value)}?`, { title: 'Sell Ship', confirmLabel: 'Sell' }).then(async ok => {
      if (!ok) return;
      await serverIntent('ship/sell', { shipId });
      // State update arrives via WS delta (ship:sold)
      this._render();
    });
  }

  // ── Resale value calculation ─────────────────────────────
  _getResaleValue(ship) {
    const basePrice = ship.type?.basePurchasePrice || 100000;
    // Condition factor: 30% at 0 condition, 70% at 100
    const conditionFactor = 0.3 + (ship.condition / 100) * 0.4;
    // Broker takes 10% commission
    const brokerCut = 0.90;
    return Math.round(basePrice * conditionFactor * brokerCut);
  }
}
